import {
  ColDef,
  GridApi,
  GridReadyEvent,
  IsRowSelectable,
  RowClassRules,
  SelectionChangedEvent,
} from "@ag-grid-community/core";
import { GenericTableRow } from "gen/model/genericTableRow";
import { useCallback, useEffect, useMemo } from "react";
import { isFirstVisibleColumn } from "shared/components/table/ag/AgGridUtils";
import { DataTableProps } from "shared/components/table/data/DataTableProps";
import {
  GenericTableAgGridReactProps,
  RowSelectionState,
} from "shared/components/table/data/DataTableTypes";
import { useMaybernTableUIContext } from "shared/components/table/maybern/MaybernTableUIContext";
import { tryGetAgGridApi } from "shared/components/table/maybern/MaybernTableUtils";

export const useDataTableRowSelectionAgProps = ({
  rowSelection,
  rowSelectionType = "multiple",
  onRowSelectionChange,
  onRowSelected,
  rowSelectionCheckboxes,
}: DataTableProps): GenericTableAgGridReactProps => {
  const isSelectionEnabled = !!(rowSelection && onRowSelectionChange);

  const { agGrid } = useMaybernTableUIContext();

  const updateSelectedRows = useCallback(
    (api: GridApi | undefined, rowSelection: RowSelectionState | undefined) => {
      if (!api || api.isDestroyed() || !rowSelection) {
        return;
      }
      Object.entries(rowSelection).forEach(([rowId, isSelected]) => {
        api.getRowNode(rowId)?.setSelected(isSelected);
      });
    },
    [],
  );

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      updateSelectedRows(event.api, rowSelection);
    },
    [rowSelection, updateSelectedRows],
  );

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent) => {
      const selectedNodes = event.api.getSelectedNodes();
      onRowSelectionChange?.(
        selectedNodes.reduce(
          (acc, node) => ({
            ...acc,
            [node.id!]: true,
          }),
          {},
        ),
      );
    },
    [onRowSelectionChange],
  );

  const isRowSelectable = useCallback<IsRowSelectable>(
    (node) =>
      isSelectionEnabled && !(node.data as GenericTableRow).child_rows?.length,
    [isSelectionEnabled],
  );

  const defaultColDef = useMemo<ColDef>(
    () =>
      isSelectionEnabled
        ? {
            checkboxSelection: (params) =>
              isFirstVisibleColumn(params.api, params.column) &&
              rowSelectionCheckboxes !== false,
          }
        : {},
    [isSelectionEnabled, rowSelectionCheckboxes],
  );

  const rowClassRules = useMemo<RowClassRules<GenericTableRow>>(
    () => ({
      "mb-row-selection-single": () => rowSelectionType === "single",
      "mb-row-selection-multiple": () => rowSelectionType === "multiple",
    }),
    [rowSelectionType],
  );

  useEffect(() => {
    updateSelectedRows(tryGetAgGridApi(agGrid), rowSelection);
  }, [agGrid, rowSelection, updateSelectedRows]);

  return {
    onGridReady,
    onSelectionChanged,
    onRowSelected,
    isRowSelectable,
    suppressRowClickSelection: true,
    rowSelection: isSelectionEnabled ? rowSelectionType : undefined,
    rowClassRules,
    defaultColDef,
  };
};
